// Libs
import { Box, Collapse, List, ListItemButton, ListItemIcon, Popover, Typography } from '@mui/material';
import classNames from 'classnames/bind';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink, useLocation } from 'react-router-dom';
// Components, Layouts, Pages
// Others
import { EMPTY_STRING } from '~/utils/constants/common';
import { hasActiveChild } from '~/utils/helper/base';
import { IRouteModel } from '~/utils/interface/base';
// Styles, images, icons
import styles from './MenuItem.module.scss';

type Props = {
  menuItem: IRouteModel;
  collapse: boolean;
};

const cx = classNames.bind(styles);

const MenuItem = (props: Props) => {
  //#region Destructuring Props
  const { path, name = EMPTY_STRING, index, icon, activeIcon, children } = props.menuItem;
  const { collapse } = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['common/sidebar', 'common/base']);
  const location = useLocation();
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [openSubmenu, setOpenSubmenu] = useState<boolean>(false);
  const [hasChildActive, setHasChildActive] = useState<boolean>(false);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  //#endregion Declare State

  //#region Declare Memo
  const isSubmenu: boolean = useMemo(() => (children ? children?.length > 0 : false), [children]);
  const isOpenPopupSubmenu = useMemo(() => Boolean(anchorEl), [anchorEl]);
  const idPopup = useMemo(() => (isOpenPopupSubmenu ? 'simple-popover' : undefined), [isOpenPopupSubmenu]);
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    if (!props.menuItem || !children) return;

    const isActive = hasActiveChild(props.menuItem, location.pathname);
    setHasChildActive(isActive);
  }, [location.pathname]);

  useEffect(() => {
    if (!collapse) return;

    setOpenSubmenu(false);
  }, [collapse]);
  //#endregion Implement Hook

  //#region Handle Function
  const toggleSubmenu = () => {
    setOpenSubmenu(!openSubmenu);
  };

  const handlePopoverOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
  };

  //#endregion Handle Function

  return (
    <>
      {/* Normal Menu */}
      {!isSubmenu && (
        <NavLink
          to={path}
          end={index}
          id='menuItemComponent'
          className={cx('menuLink', { collapse: collapse })}
        >
          {({ isActive }) => (
            <ListItemButton
              className={cx('menuBtn', { active: isActive }, { collapse: collapse })}
              selected={isActive && hasChildActive}
            >
              <ListItemIcon className={cx('menuIconWrap', { active: isActive })}>
                <img
                  src={isActive ? activeIcon : icon}
                  alt={t('common_alt_img', { ns: 'common/base' })}
                  className={cx('menuItemIcon')}
                />
              </ListItemIcon>

              <Typography
                component='p'
                className={cx('menuItemText', { active: isActive }, { collapse: collapse })}
              >
                {t(name)}
              </Typography>
            </ListItemButton>
          )}
        </NavLink>
      )}

      {/* Dropdown menu */}
      {isSubmenu && (
        <>
          {/* Sidebar expand */}
          {!collapse && (
            <>
              <ListItemButton
                id='menuItemComponent'
                className={cx('menuBtn', { active: hasChildActive })}
                onClick={toggleSubmenu}
              >
                <ListItemIcon className={cx('menuIconWrap', { active: hasChildActive })}>
                  <img
                    className={cx('menuItemIcon')}
                    src={hasChildActive ? activeIcon : icon}
                    alt={t('common_alt_img', { ns: 'common/base' })}
                  />
                </ListItemIcon>

                <Typography component='p' className={cx('menuItemText', { active: hasChildActive })}>
                  {t(name)}
                </Typography>

                <Box component={'div'} className={cx('menuItemPrev')}>
                  <Box component={'div'} className={cx('menuItemPrevIcon')}></Box>
                  <Box component={'div'} className={cx('menuItemPrevIcon', { show: openSubmenu })}></Box>
                </Box>
              </ListItemButton>

              <Collapse in={openSubmenu} timeout='auto' unmountOnExit>
                <List component='div' disablePadding>
                  {children?.map((submenu: IRouteModel) => (
                    <NavLink
                      key={submenu.path}
                      to={submenu.path}
                      end={submenu.index}
                      className={cx('menuLink', 'submenuLink')}
                    >
                      {({ isActive }) => (
                        <Typography component='p' className={cx('menuItemText', { active: isActive })}>
                          {t(submenu?.name ?? EMPTY_STRING)}
                        </Typography>
                      )}
                    </NavLink>
                  ))}
                </List>
              </Collapse>
            </>
          )}

          {/* Sidebar collapsed */}
          {collapse && (
            <>
              <ListItemButton
                id='menuItemComponent'
                className={cx('menuBtn', { active: hasChildActive }, 'collapse')}
                onClick={handlePopoverOpen}
                aria-describedby={idPopup}
              >
                <ListItemIcon className={cx('menuIconWrap', { active: hasChildActive })}>
                  <img
                    src={hasChildActive ? activeIcon : icon}
                    alt={t('common_alt_img', { ns: 'common/base' })}
                  />
                </ListItemIcon>

                <Typography
                  component='p'
                  className={cx('menuItemText', { active: hasChildActive }, 'collapse')}
                >
                  {t(name)}
                </Typography>
              </ListItemButton>

              <Popover
                id={idPopup}
                open={isOpenPopupSubmenu}
                anchorEl={anchorEl}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                onClose={handlePopoverClose}
              >
                <List component='div' disablePadding className={cx('submenuPopover')}>
                  {children?.map((submenu: IRouteModel) => (
                    <NavLink
                      key={submenu.path}
                      to={submenu.path}
                      end={submenu.index}
                      className={cx('menuLink')}
                      onClick={handlePopoverClose}
                    >
                      {({ isActive }) => (
                        <div className={cx('submenuLink', { active: isActive }, 'collapse')}>
                          <Typography
                            component='p'
                            className={cx('menuItemText', { active: isActive }, 'collapse')}
                          >
                            {t(submenu?.name ?? EMPTY_STRING)}
                          </Typography>
                        </div>
                      )}
                    </NavLink>
                  ))}
                </List>
              </Popover>
            </>
          )}
        </>
      )}
    </>
  );
};

export default MenuItem;
